import { useFadeIn } from '../../../hooks/useFadeIn';
import { useLang } from '../../../context/LanguageContext';

const brands = [
  {
    name: 'Ajax Systems',
    descRo: 'Lider mondial în sisteme wireless de alarmă inteligentă. Protecție avansată cu notificări în timp real.',
    descRu: 'Мировой лидер в беспроводных системах сигнализации. Мгновенные уведомления.',
    color: '#FF6B35',
    bgColor: 'rgba(255,107,53,0.08)',
    borderColor: 'rgba(255,107,53,0.3)',
    icon: 'ri-shield-check-fill',
  },
  {
    name: 'Hikvision',
    descRo: 'Producătorul nr. 1 mondial de camere 4K cu analiză video AI. Calitate profesională.',
    descRu: 'Производитель №1 в мире камер 4K с AI-аналитикой. Профессиональное качество.',
    color: '#E63946',
    bgColor: 'rgba(230,57,70,0.08)',
    borderColor: 'rgba(230,57,70,0.3)',
    icon: 'ri-camera-3-fill',
  },
  {
    name: 'Dahua',
    descRo: 'Sisteme CCTV complete și control acces inteligent pentru rezidențial și comercial.',
    descRu: 'Полные системы CCTV и умный контроль доступа для дома и бизнеса.',
    color: '#4CC9F0',
    bgColor: 'rgba(76,201,240,0.08)',
    borderColor: 'rgba(76,201,240,0.3)',
    icon: 'ri-eye-fill',
  },
  {
    name: 'Paradox',
    descRo: 'Centrale profesionale cu modul GSM și raportare digitală în timp real.',
    descRu: 'Профессиональные панели с GSM и цифровым мониторингом в реальном времени.',
    color: '#2DCE89',
    bgColor: 'rgba(45,206,137,0.08)',
    borderColor: 'rgba(45,206,137,0.3)',
    icon: 'ri-alarm-warning-fill',
  },
  {
    name: 'Bosch Security',
    descRo: 'Soluții enterprise de top: detectare incendiu, control acces și CCTV de precizie.',
    descRu: 'Корпоративные решения: пожарная сигнализация, контроль доступа, точный CCTV.',
    color: '#F4C430',
    bgColor: 'rgba(244,196,48,0.08)',
    borderColor: 'rgba(244,196,48,0.3)',
    icon: 'ri-building-2-fill',
  },
  {
    name: 'Ezviz',
    descRo: 'Camere smart home cloud, conectivitate mobilă 24/7, ușor de instalat și folosit.',
    descRu: 'Умные облачные камеры, мобильный доступ 24/7, простая установка и использование.',
    color: '#00FF88',
    bgColor: 'rgba(0,255,136,0.08)',
    borderColor: 'rgba(0,255,136,0.3)',
    icon: 'ri-home-4-fill',
  },
];

const certifications = [
  {
    icon: 'ri-award-fill',
    label: 'ISO 9001:2015',
    subRo: 'Calitate Certificată',
    subRu: 'Сертификат качества',
    color: '#F4C430',
  },
  {
    icon: 'ri-shield-star-fill',
    label: 'EN 50131',
    subRo: 'Standard European',
    subRu: 'Европейский стандарт',
    color: '#4CC9F0',
  },
  {
    icon: 'ri-customer-service-fill',
    label: 'Ajax Partner',
    subRo: 'Instalator Certificat',
    subRu: 'Сертифицированный монтажник',
    color: '#FF6B35',
  },
  {
    icon: 'ri-verified-badge-fill',
    label: 'Hikvision Gold',
    subRo: 'Partener Oficial',
    subRu: 'Официальный партнёр',
    color: '#E63946',
  },
];

export default function TrustBrands() {
  const ref = useFadeIn<HTMLElement>();
  const { t } = useLang();

  return (
    <section id="brands" ref={ref} className="section-fade py-16 md:py-24 bg-charcoal-100 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon/40 to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-neon/40 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-5 sm:px-6">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="slide-in-left inline-block px-4 py-1 rounded-full border border-neon/30 bg-neon/5 text-neon text-xs font-inter tracking-widest uppercase mb-4">
            {t('Mărci de Încredere', 'Бренды')}
          </span>
          <h2 className="font-rajdhani font-bold text-3xl md:text-5xl text-white">
            {t('Lucrăm cu cei mai buni', 'Работаем с лучшими')}
          </h2>
          <p className="font-inter text-cyber-muted mt-3 text-sm md:text-base max-w-lg mx-auto">
            {t(
              'Instalăm doar mărci de top, testate și certificate internațional.',
              'Устанавливаем только ведущие бренды, прошедшие международную сертификацию.'
            )}
          </p>
          <div className="mt-5 mx-auto w-16 h-0.5 bg-neon rounded-full line-grow"></div>
        </div>

        {/* Brand cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-14">
          {brands.map((brand) => (
            <div
              key={brand.name}
              className="group stagger-item flex items-start gap-4 p-5 rounded-lg border bg-charcoal transition-all duration-300 card-hover-glow cursor-default"
              style={{ borderColor: brand.borderColor, backgroundColor: brand.bgColor }}
            >
              <div
                className="w-12 h-12 flex items-center justify-center rounded-lg flex-shrink-0"
                style={{ backgroundColor: `${brand.color}20`, border: `1.5px solid ${brand.color}50` }}
              >
                <i className={`${brand.icon} text-2xl`} style={{ color: brand.color }}></i>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-rajdhani font-bold text-white text-lg mb-1">{brand.name}</h3>
                <p className="font-inter text-xs text-cyber-muted leading-relaxed">
                  {t(brand.descRo, brand.descRu)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Certifications — bigger, more prominent */}
        <div className="border-t border-charcoal-300 pt-10">
          <p className="text-center font-inter text-sm font-semibold text-white/60 mb-8 uppercase tracking-widest">
            {t('Certificări & Parteneriate', 'Сертификаты и партнёрства')}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {certifications.map((cert) => (
              <div
                key={cert.label}
                className="stagger-item flex flex-col items-center gap-3 px-4 py-5 rounded-xl border transition-all duration-300 hover:scale-105 cursor-default"
                style={{ borderColor: `${cert.color}30`, backgroundColor: `${cert.color}08` }}
              >
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-full"
                  style={{ backgroundColor: `${cert.color}15`, border: `1.5px solid ${cert.color}40` }}
                >
                  <i className={`${cert.icon} text-2xl`} style={{ color: cert.color }}></i>
                </div>
                <div className="text-center">
                  <div className="font-rajdhani font-bold text-base text-white leading-tight">{cert.label}</div>
                  <div className="font-inter text-xs text-cyber-muted mt-0.5">{t(cert.subRo, cert.subRu)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
